import { Controller } from '@hotwired/stimulus'
import MapboxGeocoder, { type Result } from '@mapbox/mapbox-gl-geocoder'
import mapboxgl, { type LngLat, type LngLatLike, type MapboxGeoJSONFeature } from 'mapbox-gl'

import { fetchWithHeaders } from 'utils'

import { GranularAllowancesError } from '../errors'
import { type HandlesPercentile } from '../interfaces'

export default class extends Controller implements HandlesPercentile {
  static targets = [
    'employeeAllowanceDisplay',
    'spouseAllowanceDisplay',
    'childAllowanceDisplay',
    'currentLocationDisplay',
    'percentileDisplay',
    'minAllowanceDisplay',
    'maxAllowanceDisplay',
  ]

  declare readonly employeeAllowanceDisplayTargets: HTMLParagraphElement[]
  declare readonly spouseAllowanceDisplayTarget: HTMLParagraphElement
  declare readonly childAllowanceDisplayTarget: HTMLParagraphElement
  declare readonly currentLocationDisplayTargets: HTMLDivElement[]
  declare readonly percentileDisplayTargets: HTMLSpanElement[]
  declare readonly minAllowanceDisplayTarget: HTMLSpanElement
  declare readonly maxAllowanceDisplayTarget: HTMLSpanElement

  static values = {
    mapboxPublicToken: String,
    mapStyle: String,
  }

  declare mapboxPublicTokenValue: string
  declare mapStyleValue: string

  declare private map: mapboxgl.Map
  declare private popup: mapboxgl.Popup
  declare private geocoder: MapboxGeocoder
  declare private geocoderResult: Result

  // TODO(mateo): Store some of this in local storage so we can persist state across page refreshes
  // Keep an in-memory cache of allowances for each rating area
  private allowancesCache: Record<string, Record<string, Record<number, number>>> = {}
  // Track current state so overlapping allowance fetches don't clobber eachother
  private currentRatingArea = 'CA16'
  private loadingAllowance = true
  private currentPercentile = 50
  private currentAge = '26'

  initialize () {
    mapboxgl.accessToken = this.mapboxPublicTokenValue

    this.popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false })
    this.geocoder = this.initializeGeocoder()
    this.map = this.initializeMap(this.geocoder)
  }

  loadInitialState = (): void => {
    const features = this.map.queryRenderedFeatures(this.map.project([-118.4065, 34.0901]), { layers: ['counties'] })
    this.selectFeature(features[0])
    this.setPopup([-118.4065, 34.0901], 'Los Angeles')
    this.updateLocationDisplay('Los Angeles')
  }

  // -------- Initialize map and geocoder --------
  initializeMap = (geocoder: MapboxGeocoder): mapboxgl.Map => {
    const map = new mapboxgl.Map({
      container: 'map',
      style: this.mapStyleValue,
      center: [-119.9, 37.3],
      zoom: 4,
      minZoom: 3,
      maxZoom: 13,
    })

    map.on('load', () => {
      this.loadInitialState()
    })

    // Handle geocoder flying to a new location (fires when moving+zooming stops)
    map.on('moveend', ({ originalEvent }) => {
      // Falsy originalEvent represents a fly-to event triggered by the geocoder
      // We only want to update the zip if we're moving to a point the user searched for, not on normal map pans
      if (!originalEvent) {
        this.handleSearchResult()
      }
    })

    // Handle clicking on a feature
    map.on('click', 'counties', (e) => {
      // Features is always defined, we clicked on a layer feature to get here
      const feature = e.features![0]

      this.handleFeatureClick(e.lngLat, feature)
    })

    // Show pointer cursor on point hover
    map.on('mouseenter', 'counties', () => {
      map.getCanvas().style.cursor = 'pointer'
    })
    map.on('mouseleave', 'counties', () => {
      map.getCanvas().style.cursor = ''
    })

    // Add the geocoder (search box) to the map
    map.addControl(geocoder)

    return map
  }

  initializeGeocoder = (): MapboxGeocoder => {
    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl,
      types: 'postcode,place,district',
      countries: 'us',
      marker: false,
      placeholder: 'Search city, zip, or county',
    })

    // Store the latest geocoder result so we can use it when we land in the new location
    // We need to wait until flying is done to use the result because we have to query the
    // visible points at the landed location.
    // There is a small race condition here, but not yet seen in practice.
    geocoder.on('result', ({ result }) => {
      this.geocoderResult = result
    })

    return geocoder
  }

  // -------- Handle map + geocoder interaction --------
  handleFeatureClick = (clickLocation: LngLat, feature: MapboxGeoJSONFeature): void => {
    this.selectFeature(feature)
    this.setPopup(clickLocation, feature.properties?.name)
    this.updateLocationDisplay(feature.properties?.name)
  }

  handleSearchResult = (): void => {
    // Get the feature at the geocoder result location.
    let features = this.map.queryRenderedFeatures(this.map.project(this.geocoderResult.center as LngLatLike), { layers: ['counties'] })
    // If we didn't find a feature, try again with no filter, just to grab what's visible on the screen
    if (features.length === 0) {
      features = this.map.queryRenderedFeatures(undefined, { layers: ['counties'] })
    }

    if (features.length > 0) {
      const feature: MapboxGeoJSONFeature = features[0]

      this.selectFeature(feature)
      this.setPopup(this.geocoderResult.center as LngLatLike, this.geocoderResult?.text)
      this.updateLocationDisplay(this.geocoderResult?.text)
    }
  }

  selectAge = (event: Event): void => {
    if (event.target instanceof HTMLSelectElement) {
      this.currentAge = event.target.value

      this.updateAllowances()
    }
  }

  selectFeature = (feature: MapboxGeoJSONFeature): void => {
    if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
      // Update allowances for location if available, otherwise first fetch then update
      this.currentRatingArea = feature.properties?.code

      this.updateAllowances()
    }
  }

  updateAllowances = (): void => {
    if (this.currentRatingArea in this.allowancesCache && this.currentAge in this.allowancesCache[this.currentRatingArea]) {
      this.displayAllowances()
    } else {
      this.fetchAndDisplayAllowances()
    }
  }

  // --------  Handle slider interaction --------
  // Should only update the current allowances, no new data fetch.
  // Ignore updates if data is still loading
  updatePercentile = (percentile: number): void => {
    this.currentPercentile = percentile

    if (!this.loadingAllowance) {
      this.displayAllowances()
    }
  }

  // -------- Update display (allowances, popup) --------
  updateLocationDisplay = (location: string): void => {
    this.currentLocationDisplayTargets.forEach(d => {
      d.innerHTML = location
    })
  }

  setPopup = (coordinates: LngLatLike, content: string): void => {
    this.popup
      .setLngLat(coordinates)
      .setHTML(`
          <div class="text-center">
            <div class="text-base font-normal">${content}</div>
            <!--<div class="text-sm font-light"></div>-->
          </div>
        `)
      .addTo(this.map)
  }

  fetchAndDisplayAllowances = (): void => {
    // Enable loading state
    this.setLoadingState(true)

    // Save current values to track state changes during fetch
    const ratingAreaCode = this.currentRatingArea
    const age = this.currentAge

    // Fetch new allowances, cache them, then run an update as normal
    fetchWithHeaders(`/public/granular_allowances?rating_area_code=${ratingAreaCode}&age=${age}&year=${new Date().getFullYear()}`)
      .then(response => {
        if (!response.ok) {
          throw new GranularAllowancesError(`Failed to fetch allowances for ${ratingAreaCode}, age ${age} (status ${response.status})`)
        }
        return response.json()
      })
      .then(body => {
        if (!(ratingAreaCode in this.allowancesCache)) {
          this.allowancesCache[ratingAreaCode] = {}
        }
        this.allowancesCache[ratingAreaCode][age] = body
        // The current rating area or age could have been updated during the fetch.
        // If it was, only cache the result and don't update the display
        if (ratingAreaCode === this.currentRatingArea && age === this.currentAge) {
          this.setLoadingState(false)
          this.displayAllowances()
        }
      })
  }

  displayAllowances = (): void => {
    // Show the slider value in the map display
    this.percentileDisplayTargets.forEach(d => {
      d.innerHTML = this.currentPercentile.toString()
    })

    // Calculate the allowances based on slider percentile
    const allowances = this.allowancesCache[this.currentRatingArea][this.currentAge]
    const allowance = allowances[this.currentPercentile]
    const minAllowance = allowances[1]
    const maxAllowance = allowances[100]

    // Display allowances
    this.updateEmployeeAllowanceDisplay(allowance)
    this.minAllowanceDisplayTarget.innerHTML = minAllowance.toString()
    this.maxAllowanceDisplayTarget.innerHTML = maxAllowance.toString()
    this.spouseAllowanceDisplayTarget.innerHTML = allowance.toString()
    this.childAllowanceDisplayTarget.innerHTML = Math.round(allowance * 0.5).toString()
  }

  setLoadingState = (loading: boolean): void => {
    if (loading) {
      this.loadingAllowance = true
      this.updateEmployeeAllowanceDisplay('...')
      this.minAllowanceDisplayTarget.innerHTML = '...'
      this.maxAllowanceDisplayTarget.innerHTML = '...'
      this.spouseAllowanceDisplayTarget.innerHTML = '...'
      this.childAllowanceDisplayTarget.innerHTML = '...'
    } else {
      this.loadingAllowance = false
    }
  }

  updateEmployeeAllowanceDisplay = (allowance: number | string): void => {
    this.employeeAllowanceDisplayTargets.forEach(d => {
      d.innerHTML = allowance.toString()
    })
  }
}
